import type {
  AgentBillingPlan,
  CreditBalanceInterval,
  FeatureType,
  Price,
} from "schemas/agent-billing";

import { isNullish } from "replo-utils/lib/misc";

import deprecatedDevStripeConfig from "./stripe/deprecated.stripe.dev";
import deprecatedProductionStripeConfig from "./stripe/deprecated.stripe.production";
import deprecatedTestStripeConfig from "./stripe/deprecated.stripe.test-mode";
import devStripeConfig, {
  agentFreeMonthCouponId as devAgentFreeMonthCouponId,
} from "./stripe/stripe.dev";
import productionStripeConfig, {
  agentFreeMonthCouponId as productionAgentFreeMonthCouponId,
} from "./stripe/stripe.production";
import testStripeConfig, {
  agentFreeMonthCouponId as testAgentFreeMonthCouponId,
} from "./stripe/stripe.test-mode";

// #region Pricing constants

/**
 * Per-message agent-ui billing markup. Multiplies the raw OpenCode cost (USD)
 * before converting to credits. 1.2 = 20% margin on top of model cost.
 * Single global value; per-model overrides are deferred (see plan).
 */
export const DEFAULT_MARKUP_MULTIPLIER = 1.2;

/**
 * Auto top-up failure backoff. After a declined charge we suppress further
 * top-up attempts for this window to avoid spamming Stripe and the user's
 * inbox on every subsequent message.
 */
export const TOPUP_FAILURE_BACKOFF_MS = 6 * 60 * 60 * 1000;

export const AGENT_FREE_MONTH_CHECKOUT_INTENT =
  "agent_free_month_retention" as const;
export type AgentFreeMonthCheckoutIntent =
  typeof AGENT_FREE_MONTH_CHECKOUT_INTENT;

// #endregion

// #region Types

type CreditFeatureConfig = {
  displayName: string;
  billingModel: "credits";
  interval: CreditBalanceInterval;
};

type CapacityFeatureConfig = {
  displayName: string;
  billingModel: "capacity";
};

type FeatureConfig = CreditFeatureConfig | CapacityFeatureConfig;

export type AgentBillingAddonProduct =
  | "sites"
  | "credits.2500"
  | "shopifyThemeBuilder.publishedElements.20"
  | "shopifyThemeBuilder.projects";

export type AgentBillingProduct = AgentBillingPlan | AgentBillingAddonProduct;

type ProductConfig = {
  featureType: FeatureType;
  units: number;
  maxProductCount: number | null;
};

type PlanConfig = {
  rank: number | null;
  plan: AgentBillingPlan | null;
  canUpgrade: boolean;
  title: string;
  features: { featureType: FeatureType; units: number | null }[];
  addOnProducts: AgentBillingAddonProduct[];
};

export type Prices = Price[];

// #endregion

// #region Features

export const features: Record<FeatureType, FeatureConfig> = {
  credits: {
    displayName: "Credit",
    billingModel: "credits",
    interval: "month",
  },
  sites: {
    displayName: "Site",
    billingModel: "capacity",
  },
  "shopifyThemeBuilder.publishedElements": {
    displayName: "Published Element",
    billingModel: "capacity",
  },
  "shopifyThemeBuilder.projects": {
    displayName: "Shopify Project",
    billingModel: "capacity",
  },
};

// #endregion

// #region Products (Stripe product key → feature mapping, used by checkout and capacity)

export const products: Record<AgentBillingAddonProduct, ProductConfig> = {
  sites: {
    featureType: "sites",
    units: 1,
    maxProductCount: null,
  },
  "credits.2500": {
    featureType: "credits",
    units: 2_500,
    maxProductCount: null,
  },
  "shopifyThemeBuilder.publishedElements.20": {
    featureType: "shopifyThemeBuilder.publishedElements",
    units: 20,
    maxProductCount: 2,
  },
  "shopifyThemeBuilder.projects": {
    featureType: "shopifyThemeBuilder.projects",
    units: 1,
    maxProductCount: null,
  },
};

// #endregion

// #region Plans

export const plans: PlanConfig[] = [
  {
    rank: null,
    plan: null,
    canUpgrade: false,
    title: "Free",
    features: [
      { featureType: "credits", units: 1_000 },
      { featureType: "sites", units: 1 },
      { featureType: "shopifyThemeBuilder.projects", units: 1 },
      { featureType: "shopifyThemeBuilder.publishedElements", units: 0 },
    ],
    addOnProducts: [],
  },
  {
    rank: 0,
    plan: "starter",
    canUpgrade: true,
    title: "Starter",
    features: [
      { featureType: "credits", units: 10_000 },
      { featureType: "sites", units: 1 },
      { featureType: "shopifyThemeBuilder.projects", units: 1 },
      { featureType: "shopifyThemeBuilder.publishedElements", units: 15 },
    ],
    addOnProducts: [
      "sites",
      "credits.2500",
      "shopifyThemeBuilder.publishedElements.20",
      "shopifyThemeBuilder.projects",
    ],
  },
  {
    rank: 1,
    plan: "pro",
    canUpgrade: true,
    title: "Pro",
    features: [
      { featureType: "credits", units: 50_000 },
      { featureType: "sites", units: 1 },
      { featureType: "shopifyThemeBuilder.projects", units: 1 },
      { featureType: "shopifyThemeBuilder.publishedElements", units: null },
    ],
    addOnProducts: ["sites", "shopifyThemeBuilder.projects", "credits.2500"],
  },
];

export function findNextPlan(
  currentPlan: AgentBillingPlan | null,
): AgentBillingPlan | null {
  const currentRank = plans.find(
    (planConfig) => planConfig.plan === currentPlan,
  )?.rank;
  const next = plans.find((planConfig) => {
    return (
      planConfig.rank !== null &&
      (isNullish(currentRank) || planConfig.rank > currentRank)
    );
  });
  return next?.plan ?? null;
}

// #endregion

// #region Stripe

/**
 * Returns the full Stripe product/price config for the current environment.
 *
 * @returns Keyed by plan name or product type, each containing price entries
 * grouped by interval.
 *
 * @example
 * getStripeConfig()
 * // => {
 * //   starter:  [{ priceId, productId, amount, currency, interval: "month" }, ...],
 * //   pro:      [...],
 * //   sites:    [...],
 * //   "credits.2500": [...],
 * //   ...
 * // }
 */
function mergeStripeConfigs(
  base: Record<AgentBillingProduct, Prices>,
  deprecated: Partial<Record<string, Prices>>,
): Record<AgentBillingProduct, Prices> {
  const result = { ...base };
  for (const [key, prices] of Object.entries(deprecated)) {
    if (!prices) {
      continue;
    }
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    const product = key as AgentBillingProduct;
    result[product] = [...(result[product] ?? []), ...prices];
  }
  return result;
}

export function getStripeConfig(): Record<AgentBillingProduct, Prices> {
  if (process.env.VITEST) {
    return mergeStripeConfigs(testStripeConfig, deprecatedTestStripeConfig);
  }
  return process.env.NODE_ENV !== "production"
    ? mergeStripeConfigs(devStripeConfig, deprecatedDevStripeConfig)
    : mergeStripeConfigs(
        productionStripeConfig,
        deprecatedProductionStripeConfig,
      );
}

export function getAgentFreeMonthCouponId(): string {
  if (process.env.VITEST) {
    return testAgentFreeMonthCouponId;
  }
  return process.env.NODE_ENV !== "production"
    ? devAgentFreeMonthCouponId
    : productionAgentFreeMonthCouponId;
}

/**
 * Returns configured prices for a given product type across all intervals.
 *
 * @returns Array of prices with interval, amount, currency, priceId, and
 * metadata flags. Empty array if the product type has no Stripe config.
 *
 * @example
 * findPrices({ productType: "starter" })
 * // => [
 * //   { interval: "month", amount: 11900, currency: "usd", priceId: "price_..." },
 * //   { interval: "year",  amount: 118800, currency: "usd", priceId: "price_..." },
 * // ]
 *
 * findPrices({ productType: "credits.2500" })
 * // => [{ interval: "one_off", amount: 2500, currency: "usd", priceId: "price_..." }]
 */
export function findPrices({
  productType,
  isSuperuser = false,
}: {
  productType: AgentBillingProduct;
  isSuperuser?: boolean;
}): Price[] {
  const prices = getStripeConfig()[productType];
  if (!prices) {
    return [];
  }
  return prices.filter((price) => isSuperuser || !price.isSuperuser);
}

/**
 * Looks up the plan configuration for a given plan name.
 *
 * @param plan - Plan name, e.g. "starter" or "pro", or null for the free/default config
 *
 * @returns The PlanConfig (rank, features, addOnProducts, etc.),
 * or undefined if no plan matches.
 *
 * @example
 * findPlanConfig("starter")
 * // => { plan: "starter", title: "Starter", features: [...], addOnProducts: [...], ... }
 *
 * findPlanConfig(null)
 * // => { plan: null, title: "Free", features: [...], addOnProducts: [], ... }
 */
export function findPlanConfig(
  plan: AgentBillingPlan | null,
): PlanConfig | undefined {
  return plans.find((planConfig) => planConfig.plan === plan);
}

/**
 * Returns how many base units of a feature are included in a plan.
 * `plan: null` = free tier. Returns `null` for unlimited, `0` if
 * the plan or feature isn't configured.
 *
 * @example
 * getIncludedBaseUnits({ plan: "starter", featureType: "credits" })
 * // => 10000
 *
 * getIncludedBaseUnits({ plan: "pro", featureType: "shopifyThemeBuilder.publishedElements" })
 * // => null  (unlimited)
 *
 * getIncludedBaseUnits({ plan: null, featureType: "credits" })
 * // => 1000  (free tier)
 */
export function getIncludedBaseUnits({
  plan,
  featureType,
}: {
  plan: AgentBillingPlan | null;
  featureType: FeatureType;
}): number | null {
  const planConfig = findPlanConfig(plan);
  if (!planConfig) {
    return 0;
  }
  const feature = planConfig.features.find(
    (planFeature) => planFeature.featureType === featureType,
  );
  if (!feature) {
    return 0;
  }
  return feature.units;
}

/**
 * Returns the set of all Stripe price IDs present in the current billing
 * config. Used to detect legacy/archived prices that are no longer valid
 * for new subscription operations.
 *
 * @example
 * getAllKnownPriceIds()
 * // => Set { "price_abc", "price_def", ... }
 */
export function getAllKnownPriceIds({
  isSuperuser = false,
}: { isSuperuser?: boolean } = {}): Set<string> {
  const priceIds = new Set<string>();
  for (const prices of Object.values(getStripeConfig())) {
    for (const price of prices) {
      if (isSuperuser || !price.isSuperuser) {
        priceIds.add(price.priceId);
      }
    }
  }
  return priceIds;
}

/**
 * Returns the set of Stripe price IDs flagged as superuser-only (the internal
 * $0 "comp" prices). These may only be added to or increased on a subscription
 * by a superuser; self-serve users can keep but never grow them.
 */
export function getSuperuserPriceIds(): Set<string> {
  const priceIds = new Set<string>();
  for (const prices of Object.values(getStripeConfig())) {
    for (const price of prices) {
      if (price.isSuperuser) {
        priceIds.add(price.priceId);
      }
    }
  }
  return priceIds;
}

/**
 * Returns the set of all Stripe product IDs that belong to agent billing
 * (plans + add-ons, across current and deprecated prices). This is the
 * canonical definition of "an agent subscription" by product — prices churn
 * constantly but the product IDs are stable — and is the same identity used
 * to scope Stripe Sigma financial queries and the MRR reconciliation.
 *
 * @example
 * getAllKnownProductIds()
 * // => Set { "prod_Pk6bs5bx3aD2ub", "prod_Pk6cMvaH3keVlH", ... }
 */
export function getAllKnownProductIds(): Set<string> {
  const productIds = new Set<string>();
  for (const prices of Object.values(getStripeConfig())) {
    for (const price of prices) {
      if (price.productId) {
        productIds.add(price.productId);
      }
    }
  }
  return productIds;
}

/**
 * Maps a raw Stripe product ID to its product type (plan or add-on) by
 * searching all product configs.
 *
 * @param stripeProductId - Stripe product ID, e.g. "prod_TuLQbLnvrHkV9V"
 *
 * @returns The AgentBillingProduct (e.g. "starter", "sites", "credits.2500"),
 * or null if the product ID doesn't match any known product.
 *
 * @example
 * getProductType({ stripeProductId: "prod_TuLQbLnvrHkV9V" })
 * // => "sites"
 *
 * getProductType({ stripeProductId: "prod_TnaWLpI4NaNOLk" })
 * // => "starter"
 *
 * getProductType({ stripeProductId: "prod_unknown" })
 * // => null
 */
export function getProductType({
  stripeProductId,
}: {
  stripeProductId: string;
}): AgentBillingProduct | null {
  for (const [productType, prices] of Object.entries(getStripeConfig())) {
    if (prices.some((price) => price.productId === stripeProductId)) {
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      return productType as AgentBillingProduct;
    }
  }
  return null;
}
